/**
 * @file sidebar-provider.js
 * @description Provider para a sidebar (CRA)
 */

import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { SidebarContext } from "../contexts/sidebar-context.js"
import { getPermissionsFromStorage } from "../utils/permissions.js"

/**
 * Provider para a sidebar
 * @param {Object} props - Props do componente
 * @param {React.ReactNode} props.children - Filhos do componente
 * @returns {React.ReactElement} Componente SidebarProvider
 */
export function SidebarProvider({ children }) {
  const [activeMenu, setActiveMenu] = useState("venda")
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [permissions, setPermissions] = useState([])
  const [permissionsLoaded, setPermissionsLoaded] = useState(false)
  const location = useLocation()
  const pathname = location.pathname
  const navigate = useNavigate()

  useEffect(() => {
    const storedPermissions = getPermissionsFromStorage()
    setPermissions(storedPermissions)
    setPermissionsLoaded(true)
  }, [])

  useEffect(() => {
    const handleStorageChange = () => {
      const storedPermissions = getPermissionsFromStorage()
      setPermissions(storedPermissions)
    }

    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [])

  useEffect(() => {
    const savedSidebarState = localStorage.getItem("sidebarOpen")
    if (savedSidebarState !== null) {
      setSidebarOpen(JSON.parse(savedSidebarState))
    }
    
    const savedCollapsedState = localStorage.getItem("sidebarCollapsed")
    if (savedCollapsedState !== null) {
      setCollapsed(JSON.parse(savedCollapsedState))
    }
  }, [])

  useEffect(() => {
    localStorage.setItem("sidebarOpen", JSON.stringify(sidebarOpen))
  }, [sidebarOpen])

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(collapsed))
  }, [collapsed])

  useEffect(() => {
    if (!permissionsLoaded) {
      return
    }

    const hasToken = localStorage.getItem("user_token")

    if (!hasToken) {
      return
    }

    if (permissions.length === 0) {
      return
    }
  }, [pathname, permissions, permissionsLoaded, navigate])

  useEffect(() => {
    if (pathname.startsWith("/usuarios")) {
      setActiveMenu("usuario")
      return
    }

    const menuMap = {
      "/": "dashboard",
      "/vendas": "venda",
      "/clientes": "cliente",
      "/servicos": "servicos",
      "/painel": "painel",
      "/circuito": "circuito",
      "/orcamento": "orcamento",
      "/configuracao": "configuracao",
    }

    const currentMenu = menuMap[pathname] || "venda"
    setActiveMenu(currentMenu)
  }, [pathname])

  const value = {
    activeMenu,
    setActiveMenu,
    sidebarOpen,
    setSidebarOpen,
    collapsed,
    setCollapsed,
  }

  return React.createElement(
    SidebarContext.Provider,
    { value: value },
    children
  )
}
